import {
  Text as MantineText,
  type TextProps as MantineTextProps,
  useComputedColorScheme,
} from '@mantine/core';
import type { ReactNode } from 'react';

type GrayScale = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
type OtherColorScale = '0' | '1' | '2' | '3' | '4' | '5' | '6';
type ColorName =
  | 'gray'
  | 'brand'
  | 'green'
  | 'red'
  | 'orange'
  | 'blue'
  | 'aqua'
  | 'yellow'
  | 'pink';

type ColorValue = `${ColorName}.${GrayScale | OtherColorScale}`;

export interface TextProps extends Omit<MantineTextProps, 'c'> {
  c?: ColorValue;
  color?: ColorValue;
  children?: ReactNode;
}

// Color mapping for dark/light mode
const colorMapping = {
  gray: {
    '0': { light: 'gray.0', dark: 'gray.9' },
    '1': { light: 'gray.1', dark: 'gray.8' },
    '2': { light: 'gray.2', dark: 'gray.7' },
    '3': { light: 'gray.3', dark: 'gray.6' },
    '4': { light: 'gray.4', dark: 'gray.5' },
    '5': { light: 'gray.5', dark: 'gray.4' },
    '6': { light: 'gray.6', dark: 'gray.3' },
    '7': { light: 'gray.7', dark: 'gray.2' },
    '8': { light: 'gray.8', dark: 'gray.1' },
    '9': { light: 'gray.9', dark: 'gray.0' },
  } as Record<GrayScale, { light: ColorValue; dark: ColorValue }>,
  brand: {
    '0': { light: 'brand.0', dark: 'brand.6' },
    '1': { light: 'brand.1', dark: 'brand.5' },
    '2': { light: 'brand.2', dark: 'brand.4' },
    '3': { light: 'brand.3', dark: 'brand.3' },
    '4': { light: 'brand.4', dark: 'brand.2' },
    '5': { light: 'brand.5', dark: 'brand.1' },
    '6': { light: 'brand.6', dark: 'brand.0' },
  } as Record<OtherColorScale, { light: ColorValue; dark: ColorValue }>,
  green: {
    '0': { light: 'green.0', dark: 'green.6' },
    '1': { light: 'green.1', dark: 'green.5' },
    '2': { light: 'green.2', dark: 'green.4' },
    '3': { light: 'green.3', dark: 'green.3' },
    '4': { light: 'green.4', dark: 'green.2' },
    '5': { light: 'green.5', dark: 'green.1' },
    '6': { light: 'green.6', dark: 'green.0' },
  } as Record<OtherColorScale, { light: ColorValue; dark: ColorValue }>,
  red: {
    '0': { light: 'red.0', dark: 'red.6' },
    '1': { light: 'red.1', dark: 'red.5' },
    '2': { light: 'red.2', dark: 'red.4' },
    '3': { light: 'red.3', dark: 'red.3' },
    '4': { light: 'red.4', dark: 'red.2' },
    '5': { light: 'red.5', dark: 'red.1' },
    '6': { light: 'red.6', dark: 'red.0' },
  } as Record<OtherColorScale, { light: ColorValue; dark: ColorValue }>,
  orange: {
    '0': { light: 'orange.0', dark: 'orange.6' },
    '1': { light: 'orange.1', dark: 'orange.5' },
    '2': { light: 'orange.2', dark: 'orange.4' },
    '3': { light: 'orange.3', dark: 'orange.3' },
    '4': { light: 'orange.4', dark: 'orange.2' },
    '5': { light: 'orange.5', dark: 'orange.1' },
    '6': { light: 'orange.6', dark: 'orange.0' },
  } as Record<OtherColorScale, { light: ColorValue; dark: ColorValue }>,
  blue: {
    '0': { light: 'blue.0', dark: 'blue.6' },
    '1': { light: 'blue.1', dark: 'blue.5' },
    '2': { light: 'blue.2', dark: 'blue.4' },
    '3': { light: 'blue.3', dark: 'blue.3' },
    '4': { light: 'blue.4', dark: 'blue.2' },
    '5': { light: 'blue.5', dark: 'blue.1' },
    '6': { light: 'blue.6', dark: 'blue.0' },
  } as Record<OtherColorScale, { light: ColorValue; dark: ColorValue }>,
  aqua: {
    '0': { light: 'aqua.0', dark: 'aqua.6' },
    '1': { light: 'aqua.1', dark: 'aqua.5' },
    '2': { light: 'aqua.2', dark: 'aqua.4' },
    '3': { light: 'aqua.3', dark: 'aqua.3' },
    '4': { light: 'aqua.4', dark: 'aqua.2' },
    '5': { light: 'aqua.5', dark: 'aqua.1' },
    '6': { light: 'aqua.6', dark: 'aqua.0' },
  } as Record<OtherColorScale, { light: ColorValue; dark: ColorValue }>,
  yellow: {
    '0': { light: 'yellow.0', dark: 'yellow.6' },
    '1': { light: 'yellow.1', dark: 'yellow.5' },
    '2': { light: 'yellow.2', dark: 'yellow.4' },
    '3': { light: 'yellow.3', dark: 'yellow.3' },
    '4': { light: 'yellow.4', dark: 'yellow.2' },
    '5': { light: 'yellow.5', dark: 'yellow.1' },
    '6': { light: 'yellow.6', dark: 'yellow.0' },
  } as Record<OtherColorScale, { light: ColorValue; dark: ColorValue }>,
  pink: {
    '0': { light: 'pink.0', dark: 'pink.6' },
    '1': { light: 'pink.1', dark: 'pink.5' },
    '2': { light: 'pink.2', dark: 'pink.4' },
    '3': { light: 'pink.3', dark: 'pink.3' },
    '4': { light: 'pink.4', dark: 'pink.2' },
    '5': { light: 'pink.5', dark: 'pink.1' },
    '6': { light: 'pink.6', dark: 'pink.0' },
  } as Record<OtherColorScale, { light: ColorValue; dark: ColorValue }>,
} as const;

export const Text = ({ c, color, children, ...props }: TextProps) => {
  const colorScheme = useComputedColorScheme('light', {
    getInitialValueInEffect: true,
  });

  let colorValue = c || color;

  // Extract size and variant to handle priority
  const { size, variant, ...otherProps } = props;

  // If size is explicitly provided, use the "default" variant that doesn't define fontSize
  const finalProps = size
    ? { size, variant: 'default', ...otherProps } // Use size with neutral variant
    : { variant, ...otherProps }; // Use variant if no size is provided

  if (colorValue) {
    const [colorName, scale] = colorValue.split('.') as [ColorName, string];
    if (colorName === 'gray') {
      const mapping = colorMapping.gray[scale as GrayScale];
      if (mapping) {
        colorValue = colorScheme === 'dark' ? mapping.dark : mapping.light;
      }
    } else {
      const mapping = colorMapping[colorName]?.[scale as OtherColorScale];
      if (mapping) {
        colorValue = colorScheme === 'dark' ? mapping.dark : mapping.light;
      }
    }

    return (
      <MantineText c={colorValue} {...finalProps}>
        {children}
      </MantineText>
    );
  }

  return <MantineText {...finalProps}>{children}</MantineText>;
};
